import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import Icon from './Icon.jsx'
import { useSitzung } from './sitzung.jsx'
import { holeThema, setzeThema, THEMEN } from './thema.js'

/**
 * Kopfzeile mit Navigation.
 *
 * Ein Tipp auf den Schriftzug „Stundenzettel“ holt die neueste Fassung der
 * App: Als installierte PWA gibt es keine Adressleiste und damit keinen
 * Neu-laden-Knopf. Der Service Worker wird zum sofortigen Wechsel gedrängt,
 * damit nicht erst beim übernächsten Start die neue Fassung erscheint.
 */
export default function Kopfzeile() {
  const { benutzer, abmelden } = useSitzung()
  const ort = useLocation()
  const [aktualisiert, setAktualisiert] = useState(false)

  const darfFremdeSehen = benutzer.rolle === 'vorgesetzter' || benutzer.rolle === 'admin'

  async function aktualisieren() {
    setAktualisiert(true)
    try {
      if ('serviceWorker' in navigator) {
        const anmeldung = await navigator.serviceWorker.getRegistration()
        if (anmeldung) {
          await anmeldung.update()
          // Eine wartende Fassung sofort übernehmen statt beim nächsten Start.
          anmeldung.waiting?.postMessage({ typ: 'sofort-aktivieren' })
        }
      }
    } catch {
      // Ohne Service Worker genügt das Neuladen.
    }
    window.location.reload()
  }

  return (
    <header className="kopf">
      <button
        type="button"
        className="marke"
        onClick={aktualisieren}
        title="Tippen, um die App zu aktualisieren"
      >
        <span className="marke__zeichen">
          <Icon name={aktualisiert ? 'aktualisieren' : 'uhr'} groesse={19} strich={2.2} />
        </span>
        <span>
          {aktualisiert ? 'Aktualisiert …' : 'Stundenzettel'}
          <span className="marke__wer">{benutzer.name}</span>
        </span>
      </button>

      <nav className="navigation">
        <Themawahl />
        {ort.pathname !== '/' && (
          <Link className="knopf knopf--leise knopf--symbol" to="/" title="Mein Monat">
            <Icon name="kalender" />
            <span className="nur-vorlesen">Mein Monat</span>
          </Link>
        )}
        {darfFremdeSehen && ort.pathname !== '/uebersicht' && (
          <Link className="knopf knopf--leise knopf--symbol" to="/uebersicht" title="Übersicht">
            <Icon name="leute" />
            <span className="nur-vorlesen">Übersicht</span>
          </Link>
        )}
        {benutzer.rolle === 'admin' && ort.pathname !== '/abrechnung' && (
          <Link className="knopf knopf--leise knopf--symbol" to="/abrechnung" title="Abrechnung">
            <Icon name="tabelle" />
            <span className="nur-vorlesen">Abrechnung</span>
          </Link>
        )}
        {benutzer.rolle === 'admin' && ort.pathname !== '/verwaltung' && (
          <Link className="knopf knopf--leise knopf--symbol" to="/verwaltung" title="Verwaltung">
            <Icon name="zahnrad" />
            <span className="nur-vorlesen">Verwaltung</span>
          </Link>
        )}
        <button
          className="knopf knopf--geist knopf--symbol"
          type="button"
          onClick={abmelden}
          title="Abmelden"
        >
          <Icon name="abmelden" />
          <span className="nur-vorlesen">Abmelden</span>
        </button>
      </nav>
    </header>
  )
}

const THEMANAME = {
  system: { symbol: 'automatik', text: 'Erscheinungsbild: wie das Gerät' },
  hell: { symbol: 'sonne', text: 'Erscheinungsbild: hell' },
  dunkel: { symbol: 'mond', text: 'Erscheinungsbild: dunkel' },
}

// Ein Knopf, der durch die drei Möglichkeiten wandert. Ein Menü wäre für drei
// Zustände zu viel Aufwand — hier genügt Weiterklicken.
function Themawahl() {
  const [thema, setThema] = useState(holeThema)

  // Beim Systemwechsel (etwa Nachtmodus des Geräts) die Statusleiste nachziehen.
  useEffect(() => {
    const abfrage = window.matchMedia('(prefers-color-scheme: dark)')
    const beiWechsel = () => setzeThema(holeThema())
    abfrage.addEventListener('change', beiWechsel)
    return () => abfrage.removeEventListener('change', beiWechsel)
  }, [])

  function weiter() {
    const naechstes = THEMEN[(THEMEN.indexOf(thema) + 1) % THEMEN.length]
    setThema(setzeThema(naechstes))
  }

  return (
    <button
      className="knopf knopf--geist knopf--symbol"
      type="button"
      onClick={weiter}
      title={THEMANAME[thema].text}
    >
      <Icon name={THEMANAME[thema].symbol} />
      <span className="nur-vorlesen">{THEMANAME[thema].text}</span>
    </button>
  )
}
